import { ref, computed } from 'vue'
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  signInWithPhoneNumber as firebaseSignInWithPhoneNumber,
  RecaptchaVerifier,
  type User as FirebaseUser,
  type ConfirmationResult
} from 'firebase/auth'
import axios from 'axios'
import { handleAuthError } from '@/utils/auth/errorMapping'

// Auth state
const currentUser = ref<FirebaseUser | null>(null)
const isAuthReady = ref(false)
const isLoading = ref(false)
const error = ref<string | null>(null)

// Phone auth state
const recaptchaVerifier = ref<RecaptchaVerifier | null>(null)
const confirmationResult = ref<ConfirmationResult | null>(null)

/**
 * Centralized authentication composable using Firebase SDK
 *
 * IMPORTANT: This composable is for SIGN-IN only (existing users).
 * For new user registration (sign-up), use useSignUp.ts composable which
 * handles backend-controlled Firebase user creation with email/phone verification.
 *
 * Responsibilities:
 * - Firebase authentication state management
 * - Google OAuth sign-in flow (existing users)
 * - Email/password authentication (existing users)
 * - Phone authentication (existing users)
 * - Password reset functionality
 * - Firebase token retrieval
 * - Logout functionality
 *
 * Note: Backend token exchange is handled by the user store (stores/user.ts)
 * This separation allows easy migration when auth logic moves to backend
 */
export function useAuth() {
  const auth = getAuth()

  // Computed properties
  const isAuthenticated = computed(() => !!currentUser.value)
  const firebaseUser = computed(() => currentUser.value)

  /**
   * Initialize auth state listener
   * Should be called once in App.vue on mount
   */
  const initAuthListener = (callback?: (user: FirebaseUser | null) => void) => {
    return onAuthStateChanged(auth, (user) => {
      currentUser.value = user
      isAuthReady.value = true

      if (callback) {
        callback(user)
      }
    })
  }

  /**
   * Sign in with Google OAuth
   * Returns Firebase ID token for backend exchange
   */
  const signInWithGoogle = async (): Promise<string | null> => {
    try {
      isLoading.value = true
      error.value = null

      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({
        prompt: 'select_account'
      })

      const result = await signInWithPopup(auth, provider)
      const idToken = await result.user.getIdToken()

      return idToken
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') return null
      error.value = handleAuthError(err, 'firebase')
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sign in with email and password
   * Returns Firebase ID token for backend exchange
   */
  const signInWithEmail = async (email: string, password: string): Promise<string | null> => {
    try {
      isLoading.value = true
      error.value = null

      const result = await signInWithEmailAndPassword(auth, email, password)
      const idToken = await result.user.getIdToken()

      return idToken
    } catch (err: any) {
      error.value = handleAuthError(err, 'firebase')
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Send password reset email
   * Returns true if email was sent successfully
   */
  const sendPasswordReset = async (email: string): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null

      await sendPasswordResetEmail(auth, email)

      return true
    } catch (err: any) {
      error.value = handleAuthError(err, 'firebase')
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Setup RecaptchaVerifier for phone authentication
   * Must be called before phone sign-in
   *
   * @param containerId - ID of DOM element to render recaptcha (e.g., 'recaptcha-container')
   * @param size - Recaptcha size: 'normal' or 'invisible'
   */
  const setupRecaptcha = (containerId: string, size: 'normal' | 'invisible' = 'invisible'): void => {
    try {
      if (recaptchaVerifier.value) {
        recaptchaVerifier.value.clear()
      }

      recaptchaVerifier.value = new RecaptchaVerifier(auth, containerId, {
        size: size,
        callback: () => {
          console.log('[useAuth] Recaptcha verified')
        },
        'expired-callback': () => {
          console.log('[useAuth] Recaptcha expired')
          error.value = 'Verification expired. Please try again.'
        }
      })
    } catch (err: any) {
      console.error('[useAuth] Recaptcha setup error:', err)
      error.value = handleAuthError(err, 'firebase')
    }
  }

  /**
   * Send SMS verification code to phone number
   * Returns true if SMS was sent successfully
   *
   * @param phoneNumber - Phone number in E.164 format (e.g., '+14155552671')
   */
  const signInWithPhone = async (phoneNumber: string): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null

      if (!recaptchaVerifier.value) {
        throw new Error('Recaptcha not initialized. Call setupRecaptcha first.')
      }

      confirmationResult.value = await firebaseSignInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifier.value
      )

      console.log('[useAuth] SMS sent successfully')
      return true
    } catch (err: any) {
      console.error('[useAuth] Phone sign-in error:', err)
      error.value = handleAuthError(err, 'firebase')

      // Reset recaptcha on error
      if (recaptchaVerifier.value) {
        try {
          recaptchaVerifier.value.clear()
          recaptchaVerifier.value = null
        } catch (clearErr) {
          console.error('[useAuth] Error clearing recaptcha:', clearErr)
        }
      }

      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Verify SMS code and complete phone authentication
   * Returns Firebase ID token for backend exchange
   *
   * @param code - 6-digit verification code from SMS
   */
  const verifyPhoneCode = async (code: string): Promise<string | null> => {
    try {
      isLoading.value = true
      error.value = null

      if (!confirmationResult.value) {
        throw new Error('No confirmation result. Call signInWithPhone first.')
      }

      const result = await confirmationResult.value.confirm(code)
      const idToken = await result.user.getIdToken()

      // Clear confirmation result after successful verification
      confirmationResult.value = null

      return idToken
    } catch (err: any) {
      console.error('[useAuth] Code verification error:', err)
      error.value = handleAuthError(err, 'firebase')
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get current user's Firebase ID token
   * Used for backend authentication
   */
  const getIdToken = async (forceRefresh = false): Promise<string | null> => {
    try {
      if (!currentUser.value) {
        return null
      }
      return await currentUser.value.getIdToken(forceRefresh)
    } catch (err: any) {
      console.error('[useAuth] Failed to get ID token:', err)
      error.value = err.message || 'Failed to get authentication token'
      return null
    }
  }

  /**
   * Sign out from Firebase
   */
  const logout = async (): Promise<void> => {
    try {
      isLoading.value = true
      error.value = null
      await signOut(auth)
      currentUser.value = null
    } catch (err: any) {
      console.error('[useAuth] Logout error:', err)
      error.value = err.message || 'Failed to sign out'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clean up recaptcha verifier
   * Should be called when component unmounts or when switching away from phone auth
   */
  const cleanupRecaptcha = () => {
    if (recaptchaVerifier.value) {
      try {
        recaptchaVerifier.value.clear()
        recaptchaVerifier.value = null
        console.log('[useAuth] Recaptcha cleaned up')
      } catch (err) {
        console.error('[useAuth] Error cleaning up recaptcha:', err)
      }
    }
    confirmationResult.value = null
  }

  /**
   * Check if a Firebase user exists for the given email
   * Used by multi-step email auth flow to determine sign-in vs sign-up
   */
  const checkEmailExists = async (email: string): Promise<{ exists: boolean; displayName?: string }> => {
    try {
      isLoading.value = true
      error.value = null

      const response = await axios.post<{ exists: boolean; display_name?: string }>('/api/3/check-email', { email })
      return { exists: response.data.exists, displayName: response.data.display_name ?? undefined }
    } catch (err: any) {
      console.error('[useAuth] Check email error:', err)
      error.value = 'Failed to check email. Please try again.'
      return { exists: false }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear error message
   */
  const clearError = () => {
    error.value = null
  }

  return {
    // State
    currentUser: firebaseUser,
    isAuthenticated,
    isAuthReady,
    isLoading,
    error,

    // Methods
    initAuthListener,
    signInWithGoogle,
    signInWithEmail,
    checkEmailExists,
    sendPasswordReset,
    setupRecaptcha,
    signInWithPhone,
    verifyPhoneCode,
    cleanupRecaptcha,
    getIdToken,
    logout,
    clearError,
  }
}
